import { OrderStatus } from '@/resources/enums'
import { HexaV2ProgressColor, HexaV2TextColor } from '@/resources/enums/hexaV2'
import { OrderColorHex, OrderColors } from '@/resources/enums/orders'
import {
  HexaV2OrderProgressColor,
  HexaV2OrderTextColor,
  OrderColor,
  HexaV2OrderColorTimeInSeparationTurbo,
  HexaV2OrderColorTimeInSeparation,
  HexaV2OrderColorTimeOnGoingTurbo,
  HexaV2OrderColorTimeOnGoing,
} from '@/resources/constants/orders'

const ONE_HOUR_TIMESTAMP = 60 * 60 * 1000
const ONE_AND_A_HALF_HOUR_TIMESTAMP = 90 * 60 * 1000

const HALF_HOUR_TIMESTAMP = 30 * 60 * 1000
const FORT_FIVE_MINUTES_TIMESTAMP = 45 * 60 * 1000

export type HexaV2Colors = {
  text: HexaV2TextColor
  progress: HexaV2ProgressColor
}

export const getScheduledColorByTime = (endDeliveryDate: Date): OrderColors => {
  const currentDate = new Date()
  const oneHourFromNow = new Date(currentDate.getTime() + ONE_HOUR_TIMESTAMP)
  const oneHourAndAHalfFromNow = new Date(
    currentDate.getTime() + ONE_AND_A_HALF_HOUR_TIMESTAMP
  )

  if (endDeliveryDate <= oneHourFromNow) {
    return OrderColors.RED
  }
  if (endDeliveryDate <= oneHourAndAHalfFromNow) {
    return OrderColors.YELLOW
  }
  return OrderColors.GREEN
}

export const getPickupColorByTime = (pickupInitialDate: Date): OrderColors => {
  const currentDate = new Date()

  const HalfHourFromNow = new Date(
    pickupInitialDate.getTime() + HALF_HOUR_TIMESTAMP
  )

  const FortFiveMinutesFromNow = new Date(
    pickupInitialDate.getTime() + FORT_FIVE_MINUTES_TIMESTAMP
  )

  if (currentDate >= FortFiveMinutesFromNow) {
    return OrderColors.RED
  }

  if (currentDate >= HalfHourFromNow) {
    return OrderColors.YELLOW
  }

  return OrderColors.GREEN
}

export const getScheduledColorTextByTime = (
  endDeliveryDate: Date
): OrderColorHex => {
  const color = getScheduledColorByTime(endDeliveryDate)
  return OrderColor[color]
}

export const getHexaV2ColorsByTime = (color: OrderColors): HexaV2Colors => {
  return {
    text: HexaV2OrderTextColor[color],
    progress: HexaV2OrderProgressColor[color],
  }
}

export const isInSeparationOrder = (orderStatus: OrderStatus) =>
  [OrderStatus.POC_ACCEPTED, OrderStatus.DM_ACCEPTED].includes(orderStatus)

export const isOnGoingOrder = (orderStatus: OrderStatus) =>
  [OrderStatus.DM_PICKED_UP, OrderStatus.DM_EN_ROUTE].includes(orderStatus)

const getInSeparationOrderTimeLimis = (isTurbo = false) =>
  isTurbo
    ? HexaV2OrderColorTimeInSeparationTurbo
    : HexaV2OrderColorTimeInSeparation

const getOnGoingOrderTimeLimis = (isTurbo = false) =>
  isTurbo ? HexaV2OrderColorTimeOnGoingTurbo : HexaV2OrderColorTimeOnGoing

const getOrderTimeColor = (
  orderStatus: string | OrderStatus,
  minutes: number,
  isTurbo: boolean
) => {
  if (!Object.keys(OrderStatus).includes(orderStatus)) return OrderColors.RED

  let timeLimits: { [key in OrderColors]: number }

  if (isInSeparationOrder(orderStatus as OrderStatus)) {
    timeLimits = getInSeparationOrderTimeLimis(isTurbo)
  } else if (isOnGoingOrder(orderStatus as OrderStatus)) {
    timeLimits = getOnGoingOrderTimeLimis(isTurbo)
  } else {
    return OrderColors.RED
  }

  if (minutes <= timeLimits[OrderColors.GREEN]) return OrderColors.GREEN
  if (minutes <= timeLimits[OrderColors.YELLOW]) return OrderColors.YELLOW
  return OrderColors.RED
}

export const getOrderTimeColorHex = (
  orderStatus: string | OrderStatus,
  minutes: number,
  isTurbo = false
): OrderColorHex => {
  const color = getOrderTimeColor(orderStatus, minutes, isTurbo)
  return OrderColor[color]
}

export const getTimeColor = (
  orderStatus: string | OrderStatus,
  minutes: number,
  isTurbo = false
): HexaV2ProgressColor => {
  const color = getOrderTimeColor(orderStatus, minutes, isTurbo)
  return HexaV2OrderProgressColor[color]
}
